import { Link } from '@mui/joy';
import { createFileRoute } from '@tanstack/react-router';
import { Fragment } from 'react';

import ActivationNotificationImage from '@/assets/help/activation-notification.png';
import AddUserImage from '@/assets/help/add-user.png';
import LkrActivateMunicipalitiesImage from '@/assets/help/lkr-activate-municipalities.png';
import LkrMenuImage from '@/assets/help/lkr-menu.png';
import RegistrationEndImage from '@/assets/help/registration-end.png';
import RegistrationInputImage from '@/assets/help/registration-input.png';
import { LegalHeading } from '@/components/legal/LegalHeading';
import { LegalText } from '@/components/legal/LegalText';
import { makeMetaTitle } from '@/modules/META';

export const Route = createFileRoute('/_content/schnellstart')({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: RouteComponent,
  head: () => ({
    meta: [makeMetaTitle(['Schnellstart'])],
  }),
});

function RouteComponent() {
  return (
    <Fragment>
      <LegalHeading level="h1" sx={{ marginBlockStart: 0 }}>
        Willkommen im RadMELDER | Die ersten Schritte
      </LegalHeading>

      <LegalText
        component="ol"
        sx={(theme) => ({
          counterReset: 'list-counter',
          listStyle: 'none',
          paddingLeft: 0,
          '& > li': {
            alignItems: 'flex-start',
            counterIncrement: 'list-counter',
            display: 'flex',
            '&::before': {
              backgroundColor: theme.palette.primary.solidBg,
              borderRadius: '50%',
              color: theme.palette.background.surface,
              content: 'counter(list-counter)',
              flexShrink: 0,
              fontSize: '18px',
              height: theme.spacing(3),
              lineHeight: theme.spacing(3),
              marginRight: theme.spacing(2),
              marginTop: '5px',
              paddingTop: '1px',
              textAlign: 'center',
              width: theme.spacing(3),
            },
          },
        })}
      >
        <li>
          <div>
            <LegalText color="primary" level="h2">
              Registrieren als Kommune oder Landkreis
            </LegalText>

            <LegalText>
              Melden Sie sich mit Ihrem Service-BW-Konto an und hinterlegen Sie die zuständige(n) Verwaltungsebene(n).
            </LegalText>

            <img alt="Registrierung Eingabe" src={RegistrationInputImage} style={{ maxWidth: 600, marginBottom: 20 }} />

            <LegalText>
              <b>Aktion</b>: Registrierung abschließen
            </LegalText>

            <img alt="Registrierung Abschluss" src={RegistrationEndImage} style={{ maxWidth: 600, marginBottom: 20 }} />
          </div>
        </li>
        <li>
          <div>
            <LegalText color="primary" level="h2">
              Freischaltung abwarten
            </LegalText>

            <LegalText>
              Nach der Erstregistrierung wird Ihr Account und Ihre Verwaltungsebene durch die RadMELDER-Administration
              oder von ManagerInnen ihrer Verwaltungsebene geprüft und freigeschaltet.
            </LegalText>

            <LegalText>
              <b>Hinweis</b>: Sie erhalten eine Benachrichtigung per E-Mail.
            </LegalText>

            <img
              alt="Freischaltung abwarten"
              src={ActivationNotificationImage}
              style={{ maxWidth: 600, marginBottom: 20 }}
            />

            <LegalText>
              <b>Aktion</b>: Einloggen, Profileinstellungen vornehmen und Meldungsbearbeitung starten.
            </LegalText>
          </div>
        </li>
        <li>
          <div>
            <LegalText color="primary" level="h2">
              Kreisangehörige Kommunen als Landkreis aktivieren
            </LegalText>

            <LegalText>
              Landkreise können untergeordnete Kommunen verwalten und deren Meldungsbearbeitung übernehmen.
            </LegalText>
            <LegalText>
              <b>Aktion</b>: Kommunen in der Verwaltungsebene prüfen und aktivieren.
            </LegalText>
            <img alt="Landkreis Menü" src={LkrMenuImage} style={{ maxWidth: 600, marginBottom: 20 }} />

            <img
              alt="Untergeordnete Verwaltungsebenen"
              src={LkrActivateMunicipalitiesImage}
              style={{ maxWidth: 600, marginBottom: 20 }}
            />
          </div>
        </li>
        <li>
          <div>
            <LegalText color="primary" level="h2">
              Als Manager einer Verwaltungsebene weitere Benutzer freischalten
            </LegalText>

            <LegalText>
              Neue Benutzerinnen und Benutzer werden über den Freischaltungslink in der E-Mail aktiviert.
            </LegalText>
            <LegalText>
              <b>Prüfen Sie dabei:</b>
            </LegalText>
            <LegalText component="ul">
              <li>Benutzerstatus = Aktiv</li>
              <li>Zugeordnete Verwaltungsebene korrekt hinterlegt</li>
              <li>Zugeordnete Verwaltungsebene = Aktiv</li>
              <li>Benutzerberechtigung in Verwaltungsebene aktiv geschaltet und Rolle definiert </li>
            </LegalText>
            <img alt="Benutzer*in hinzufügen" src={AddUserImage} style={{ maxWidth: '100%', marginBottom: 20 }} />
            <LegalText>
              <b>Aktion</b>: Eingaben speichern
            </LegalText>
          </div>
        </li>
        <li>
          <div>
            <LegalText color="primary" level="h2">
              Externe Anbindung nutzen
            </LegalText>
            <LegalText>
              Der RadMELDER verfügt über eine Schnittstelle im Standard{' '}
              <Link href="https://wiki.open311.org/GeoReport_v2/">Open311 Georeport V2</Link>, die eine
              Meldungsweitergabe an externe Meldeplattformen ermöglicht. Kommunen, die bereits eine eigene
              Meldeplattform nutzen, können diese via Schnittstelle mit dem RadMELDER verknüpfen. Dafür muss im
              jeweiligen Fremdsystem ein Schnittstellen-Endpunkt im selben Standard eingerichtet werden. Nach Eingabe
              der Gegenstelle können Meldungen via RadMELDER eingegeben und automatisch an das entsprechende Zielsystem
              weitergeleitet werden.
            </LegalText>
            <LegalText>
              Verbinden Sie bestehende Mängelmeldersysteme mit dem RadMELDER und nutzen Sie die verfügbaren
              Integrationsmöglichkeiten.
            </LegalText>
            <LegalText>
              <b>Funktionsweise</b>
            </LegalText>
            <LegalText>Die Schnittstellenanbindung ermöglicht für inaktive RadMELDER-Kommunen:</LegalText>
            <LegalText component="ul">
              <li>eine Meldungseingabe im RadMELDER mit</li>
              <li>Abfrage von Meldungskategorien aus dem externen System (GET Service List),</li>
              <li>anschließender Weiterleitung der Meldung an das jeweilige Zielsystem (POST Service Request) und</li>
              <li>
                Statusabfrage der Meldung am Zielsystem (GET Service Request), um den Meldungsstatus im RadMELDER
                aktuell zu halten.
              </li>
            </LegalText>
            <LegalText>
              <b>Voraussetzungen</b>
            </LegalText>
            <LegalText component="ul">
              <li>Umsetzung der Open311 GeoReport V2 Schnittstelle im Fremdsystem durch den jeweiligen Betreiber</li>
              <li>
                Bekanntgabe des Schnittstellen-Endpunkts und ggf. der juristiction_id (wenn dort mehrere
                Kommunen/Organisationen konfiguriert sind) an das RadMELDER-Team
              </li>
            </LegalText>
          </div>
        </li>
      </LegalText>
    </Fragment>
  );
}
